import { Row } from '@tanstack/react-table'
import { Eye, Pencil, Trash2, Archive, ArchiveRestore } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { RowActionBar, type RowAction } from '@/components/ui/row-action-bar'
import { useCases } from '../context/client-context'
import { Case } from '../data/schema'
import { useTranslation } from 'react-i18next'
import { UserPermission } from '@/constants/permissions'
import { useAuth } from '@/stores/authStore'

interface DataTableRowActionsProps {
  row: Row<Case>
}

export function DataTableRowActions({ row }: DataTableRowActionsProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setOpen, setCurrentRow } = useCases()
  const { user, can } = useAuth()
  const caseData = row.original

  const isOwner = user?.id === caseData.assigned_to?.id || user?.id === caseData.added_by?.id

  const canEdit = can(UserPermission.CHANGE_CASE) || (can(UserPermission.CHANGE_OWN_CASE) && isOwner)
  const canArchive = can(UserPermission.ARCHIVE_CASE)
  const canDelete = can(UserPermission.DELETE_CASE) || (can(UserPermission.DELETE_OWN_CASE) && isOwner)

  const actions: RowAction[] = [
    {
      key: 'view',
      icon: <Eye className="h-4 w-4" />,
      label: t('view'),
      onClick: () =>
        navigate({
          to: `/cases/${caseData.id}`,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          state: { caseData, timestamp: Date.now() } as any,
        }),
    },
    {
      key: 'edit',
      icon: <Pencil className="h-4 w-4" />,
      label: t('edit'),
      hidden: !canEdit,
      onClick: () => {
        setCurrentRow(caseData)
        setOpen('edit')
      },
    },
    {
      key: 'archive',
      icon: caseData.archived_at ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />,
      label: caseData.archived_at ? t('unarchive') : t('archive'),
      hidden: !canArchive,
      onClick: () => {
        setCurrentRow(caseData)
        setOpen('archive')
      },
    },
    {
      key: 'delete',
      icon: <Trash2 className="h-4 w-4" />,
      label: t('delete'),
      hidden: !canDelete,
      destructive: true,
      onClick: () => {
        setCurrentRow(caseData)
        setOpen('delete')
      },
    },
  ]

  return <RowActionBar actions={actions} />
}
